import React, { useRef, useEffect, useState } from 'react';
import { IMPACT_STATS, VENTURES } from '../../data/mock';
import { ExternalLink, Check } from 'lucide-react';
import { useIsMobile, useIsTablet } from '../../hooks/useWindowSize';

const useInView = (threshold = 0.08) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); }, { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, inView];
};

const ImpactSection = () => {
  const [statsRef, statsInView] = useInView(0.05);
  const [venturesRef, venturesInView] = useInView(0.03);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  const pad = isMobile ? '0 20px' : isTablet ? '0 32px' : '0 48px';
  const secPad = isMobile ? '60px 0' : '80px 0';

  return (
    <>
      {/* Impact Stats */}
      <section ref={statsRef} style={{ background: '#0A0A0A', padding: secPad }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: pad }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{ width: '36px', height: '3px', background: '#E62B1E' }} />
            <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 700, letterSpacing: '3px', color: '#E62B1E', textTransform: 'uppercase' }}>By the Numbers</span>
          </div>
          <h2 style={{
            fontFamily: '"Bebas Neue", sans-serif',
            fontSize: isMobile ? '34px' : 'clamp(36px, 4vw, 56px)',
            color: '#FFFFFF', letterSpacing: '2px', margin: '0 0 40px 0', lineHeight: 1
          }}>IMPACT THAT SPEAKS</h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            border: '1px solid #1A1A1A', borderRadius: '8px', overflow: 'hidden'
          }}>
            {IMPACT_STATS.map((stat, i) => (
              <div key={i} style={{
                padding: isMobile ? '28px 20px' : '44px 36px',
                background: '#0E0E0E',
                borderRight: isMobile
                  ? (i % 2 !== 1 ? '1px solid #1A1A1A' : 'none')
                  : (i % 3 !== 2 ? '1px solid #1A1A1A' : 'none'),
                borderBottom: isMobile
                  ? (i < 4 ? '1px solid #1A1A1A' : 'none')
                  : (i < 3 ? '1px solid #1A1A1A' : 'none'),
                opacity: statsInView ? 1 : 0,
                transform: statsInView ? 'translateY(0)' : 'translateY(20px)',
                transition: `opacity 0.6s ease ${i * 0.08}s, transform 0.6s ease ${i * 0.08}s`
              }}
                onMouseEnter={e => { e.currentTarget.style.background = '#161616'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#0E0E0E'; }}
              >
                <p style={{
                  fontFamily: '"Bebas Neue", sans-serif',
                  fontSize: isMobile ? '36px' : 'clamp(40px, 4vw, 56px)',
                  color: '#E62B1E', margin: '0 0 4px 0', letterSpacing: '2px', lineHeight: 1
                }}>{stat.value}</p>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: isMobile ? '13px' : '15px', fontWeight: 600, color: '#FFFFFF', margin: '0 0 3px 0' }}>{stat.label}</p>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: '#4A4A4A', margin: 0 }}>{stat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ventures */}
      <section id="ventures" ref={venturesRef} style={{ background: '#F5F3EF', padding: isMobile ? '64px 0' : '100px 0' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: pad }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{ width: '36px', height: '3px', background: '#E62B1E' }} />
            <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 700, letterSpacing: '3px', color: '#E62B1E', textTransform: 'uppercase' }}>Ventures</span>
          </div>
          <h2 style={{
            fontFamily: '"Bebas Neue", sans-serif',
            fontSize: isMobile ? '34px' : 'clamp(36px, 4vw, 56px)',
            color: '#0A0A0A', letterSpacing: '2px', margin: '0 0 40px 0', lineHeight: 1
          }}>WHAT I'M BUILDING</h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {VENTURES.map((v, i) => (
              <div key={i} style={{
                background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.06)',
                borderRadius: '8px', overflow: 'hidden',
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : isTablet ? '220px 1fr' : '260px 1fr',
                boxShadow: '0 2px 20px rgba(0,0,0,0.04)',
                opacity: venturesInView ? 1 : 0,
                transform: venturesInView ? 'translateX(0)' : 'translateX(-20px)',
                transition: `opacity 0.7s ease ${i * 0.15}s, transform 0.7s ease ${i * 0.15}s`
              }}>
                {/* Left accent */}
                <div style={{
                  background: v.color,
                  padding: isMobile ? '28px 24px' : '40px 28px',
                  display: 'flex', flexDirection: isMobile ? 'row' : 'column',
                  justifyContent: isMobile ? 'space-between' : 'space-between',
                  alignItems: isMobile ? 'center' : 'flex-start',
                  gap: '12px'
                }}>
                  <div>
                    <span style={{
                      fontFamily: 'Inter, sans-serif', fontSize: '10px',
                      color: 'rgba(255,255,255,0.6)', fontWeight: 600,
                      letterSpacing: '2px', textTransform: 'uppercase',
                      display: 'block', marginBottom: isMobile ? '4px' : '10px'
                    }}>{v.period}</span>
                    <h3 style={{
                      fontFamily: '"Bebas Neue", sans-serif',
                      fontSize: isMobile ? '22px' : '24px', color: '#FFFFFF',
                      letterSpacing: '1px', margin: '0 0 4px 0', lineHeight: 1
                    }}>{v.name}</h3>
                    {!isMobile && <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: 'rgba(255,255,255,0.72)', margin: 0 }}>{v.role}</p>}
                  </div>
                  <div style={{
                    display: 'inline-block', background: 'rgba(255,255,255,0.12)',
                    borderRadius: '4px', padding: '4px 10px', whiteSpace: 'nowrap'
                  }}>
                    <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '10px', color: 'rgba(255,255,255,0.8)' }}>{v.tag}</span>
                  </div>
                </div>

                {/* Right content */}
                <div style={{ padding: isMobile ? '24px' : isTablet ? '32px' : '36px 44px' }}>
                  {isMobile && <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#9A9A9A', margin: '0 0 6px 0' }}>{v.role}</p>}
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: isMobile ? '14px' : '15px', color: '#3A3A3A', lineHeight: 1.75, margin: '0 0 20px 0' }}>{v.description}</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {v.highlights.map((h, j) => (
                      <div key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: '9px' }}>
                        <div style={{ marginTop: '3px', flexShrink: 0 }}><Check size={12} color="#E62B1E" /></div>
                        <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#5A5A5A', lineHeight: 1.55 }}>{h}</span>
                      </div>
                    ))}
                  </div>
                  {v.website && (
                    <a href={`https://${v.website}`} target="_blank" rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                        marginTop: '20px', color: '#E62B1E',
                        fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 600,
                        textDecoration: 'none', transition: 'opacity 0.2s ease'
                      }}
                      onMouseEnter={e => { e.currentTarget.style.opacity = '0.65'; }}
                      onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                    >{v.website} <ExternalLink size={11} /></a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ImpactSection;
